/* eslint-disable consistent-return */

import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { WebSocketLink } from '@apollo/client/link/ws';
import { getMainDefinition } from '@apollo/client/utilities';
import { SubscriptionClient } from 'subscriptions-transport-ws';
import { sentryApolloLink } from '../observability/apolloLink';
import { getGraphqlUrl } from '../utils/backendOverride';

const httpLink = new HttpLink({
  // Resolved per request: the A/B company is routed to the sin-Prisma backend.
  // Subscriptions stay on the env backend on purpose (A/B covers REST +
  // GraphQL HTTP only; both backends share the same MongoDB).
  uri: () => getGraphqlUrl(),
});

const wsLink = new WebSocketLink(
  new SubscriptionClient(process.env.REACT_APP_GRAPHQL__SUBSCRIPTION_URL, {
    reconnect: true,
    lazy: true,
    connectionParams: async () => {
      const token = localStorage.getItem('token');
      return {
        headers: {
          authorization: token ? `Bearer ${token}` : '',
          Connection: 'Upgrade',
          Upgrade: 'websocket',
        },
      };
    },
  }),
);

const authLink = setContext((_, { headers }) => {
  const token = localStorage.getItem('token');
  return {
    headers: {
      ...headers,
      authorization: token ? `Bearer ${token}` : '',
    },
  };
});

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition'
      && definition.operation === 'subscription'
    );
  },
  wsLink,
  httpLink,
);

const apolloClient = new ApolloClient({
  link: ApolloLink.from([sentryApolloLink, authLink, link]),
  cache: new InMemoryCache(),
});

const apolloQuery = async (query, variables) => {
  try {
    const response = await apolloClient.query({ query, variables, fetchPolicy: 'no-cache' });
    return response;
  } catch (error) {
    if (error?.graphQLErrors?.[0]?.message === 'INVALID_TOKEN') {
      localStorage.clear();
      window.location.replace('/');
    } else throw error;
  }
};

const apolloMutation = async (mutation, variables) => {
  try {
    const response = await apolloClient.mutate({ mutation, variables, fetchPolicy: 'no-cache' });
    return response;
  } catch (error) {
    if (error?.graphQLErrors?.[0]?.message === 'INVALID_TOKEN') {
      localStorage.clear();
      window.location.replace('/');
    } else throw error;
  }
};

const client = {
  query: (operation) => apolloQuery(operation.query, operation.variables),
  mutate: (operation) => apolloMutation(operation.mutation, operation.variables),
  subscribe: (operation) => apolloClient.subscribe({ ...operation, fetchPolicy: 'no-cache' }),
};


export default client;
